'use client';

import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../ui/carousel';

const FeaturedOn = () => {
  const snapCount = Math.ceil(configs.length / 2);
  const moreThanOne = snapCount > 1;
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const pointerDownHandler = () => {
      if (moreThanOne) {
        (carouselApi?.plugins() as unknown as any)?.autoplay?.stop();
      }
    };

    const pointerUpHandler = () => {
      if (moreThanOne) {
        (carouselApi?.plugins() as unknown as any)?.autoplay?.play();
      }
    };

    carouselApi.on('pointerDown', pointerDownHandler);
    carouselApi.on('pointerUp', pointerUpHandler);

    return () => {
      carouselApi.off('pointerDown', pointerDownHandler);
      carouselApi.off('pointerUp', pointerUpHandler);
    };
  }, [carouselApi, moreThanOne]);

  return (
    <div className='py-10 lg_py-[100px]'>
      <h2 className='text-center font-Shango text-[26px] leading-8 lg_text-[28px] lg_leading-[34px]'>FEATURED ON</h2>
      <div className={clsx(' mt-[25px] ', 'relative', 'lg_hidden')}>
        <Carousel
          opts={{
            loop: true,
          }}
          setApi={setCarouselApi}
          plugins={[
            Autoplay({
              playOnInit: true,
              delay: 3000,
            }),
          ]}
        >
          <CarouselContent>
            {configs.map((_, i, arr) => {
              if (i % 2 === 0) {
                return (
                  <CarouselItem key={i}>
                    <div className='flex items-center justify-center gap-x-5'>
                      {arr.slice(i, i + 2).map(({ src, alt, width, height, href }, i) => {
                        return href ? (
                          <Link
                            key={`${src}-${i}`}
                            className='block'
                            href={href}
                          >
                            <Image
                              src={src}
                              alt={alt}
                              width={width}
                              height={height}
                            />
                          </Link>
                        ) : (
                          <Image
                            src={src}
                            key={`${src}-${i}`}
                            alt={alt}
                            width={width}
                            height={height}
                          />
                        );
                      })}
                    </div>
                  </CarouselItem>
                );
              }

              return null;
            })}
          </CarouselContent>
        </Carousel>
        <CarouselDots
          api={carouselApi}
          snapCount={snapCount}
        />
      </div>
      <div className='mx-auto mt-10 hidden max-w-[1440px] items-center justify-center gap-x-[60px] lg_flex'>
        {configs.map(({ src, alt, width, height, href }, i) => {
          return href ? (
            <Link
              key={i}
              className='block'
              href={href}
            >
              <Image
                src={src}
                alt={alt}
                width={width}
                height={height}
              />
            </Link>
          ) : (
            <Image
              key={i}
              src={src}
              alt={alt}
              width={width}
              height={height}
            />
          );
        })}
      </div>
    </div>
  );
};

const useDotButton = (emblaApi: CarouselApi | null) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }

      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onInit = (emblaApi: CarouselApi) => {
      if (!emblaApi) {
        return;
      }

      setScrollSnaps(emblaApi.scrollSnapList());
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    const onSelect = (emblaApi: CarouselApi) => {
      if (!emblaApi) {
        return;
      }

      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onInit);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

const CarouselDots = (props: { api: CarouselApi | null; snapCount: number }) => {
  const { api, snapCount } = props;

  const { selectedIndex, onDotButtonClick } = useDotButton(api);

  if (snapCount <= 1) {
    return null;
  }

  return (
    <div className='mt-[15px] flex items-center justify-center lg_mt-5 lg_hidden'>
      {Array.from(
        {
          length: snapCount,
        },
        (_, i) => {
          return (
            <button
              type='button'
              onClick={() => {
                onDotButtonClick(i);
              }}
              key={i}
              className={clsx(
                'relative h-6 w-6 ',
                'after_absolute after_left-1/2 after_top-1/2 after_h-2 after_w-2 after_rounded-[50%] after_content-[""]',
                'after_border-1 after_border-solid after_border-BE9983',
                'after_-translate-x-1/2 after_-translate-y-1/2',
                selectedIndex === i ? 'after_bg-BE9983' : 'after_bg-transparent',
              )}
              aria-label={`carousel dot ${i + 1}`}
            />
          );
        },
      )}
    </div>
  );
};

const configs = [
  {
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/featured-on-the-knot.png`,
    alt: 'seen in love inc',
    width: 100,
    height: 100,
    href: 'https://www.theknot.com/fashion/aw-bridal-bridesmaid-dresses',
  },
  {
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/featured-on-brides.png`,
    alt: 'brides',
    width: 101,
    height: 100,
  },
  {
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/featured-on-wedding-forward.png`,
    alt: 'wedding-forward',
    width: 101,
    height: 100,
    href: 'https://www.weddingforward.com/aw-bridal-bridesmaid-dresses/',
  },
  {
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/featured-on-green-wedding-shoes.png`,
    alt: 'green-wedding-shoes',
    width: 97,
    height: 99,
    href: 'https://greenweddingshoes.com/satin-bridesmaids-dresses-from-aw-bridal/',
  },
];

export default FeaturedOn;
